import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, EnvironmentProviders, LOCALE_ID, provideBrowserGlobalErrorListeners, Provider, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, Route, Routes, withInMemoryScrolling } from '@angular/router';
import { IonicStorageModule } from '@ionic/storage-angular';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AppLayoutData } from '@spotter/components/layout/app-layout/app-layout.component';
import { SpotterEnvironment } from '@spotter/environments/environment.template';
import { SiteShorthand } from '@spotter/environments/sites';
import { IsAdminOrOwnerGuard, LoggedOutGuard } from '@spotter/guards/login-guard';
import { LanguageInitialiser } from '@spotter/inits/language-initialiser';
import { SpotterMissingTranslationHandler, SpotterTranslationLoaderFactory } from '@spotter/inits/translation-initialiser';
import { UserInisitaliser } from '@spotter/inits/user-initialiser';
import { SpotterHttpInterceptor } from '@spotter/interceptors/http-interceptor';
import { TokenInterceptor } from '@spotter/interceptors/token-interceptor';
import { TranslationLinkDelegationService } from '@spotter/interceptors/trans-link-interceptor';
import { LanguageService } from '@spotter/services/internal/language-service';
import { SPOTTER_ENV } from '@spotter/tokens/env.token';
import { MatomoModule, MatomoRouterModule } from 'ngx-matomo-client';

/** Adds the required providers for the app.
 *  This includes loading the user and languages, and setting up the HTTP interceptors.
 *  Optionally include a callback function to get a ping when an APP_INITIALIZER is complete
 */
export function addSpotterProviders(routes: Routes, environment: SpotterEnvironment, updateLoader?: Function): (Provider | EnvironmentProviders)[] {
    return [

        // Global spotter providers
        { provide: SPOTTER_ENV, useValue: environment },
        { provide: HTTP_INTERCEPTORS, useClass: SpotterHttpInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        {
            provide: APP_INITIALIZER,
            multi: true,
            deps: [TranslationLinkDelegationService],
            useFactory: (svc: TranslationLinkDelegationService) => () => svc.start(),
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (appInitService: UserInisitaliser) => () => appInitService.initialise(updateLoader),
            multi: true,
            deps: [UserInisitaliser]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (appInitService: LanguageInitialiser) => () => appInitService.initialise(updateLoader),
            multi: true,
            deps: [LanguageInitialiser]
        },
        {
            provide: LOCALE_ID,
            deps: [LanguageService],
            useFactory: (lang: LanguageService) => lang.registerAndLoadLanguage() //TODO: Check if this does anything and should it be an APP_INIT
        },

        // Angular setup
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
        provideHttpClient(withInterceptorsFromDi()),


        // Libraries
        IonicStorageModule.forRoot().providers!,

        MatomoModule.forRoot({
            siteId: environment.matamoSiteID,
            trackerUrl: environment.matamoSiteURL,
            disabled: !environment.enableAnalytics
        }).providers!,
        MatomoRouterModule.forRoot({
            exclude: ["data\/monthly-report", "resetpassword\/"],
        }).providers!,

        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (SpotterTranslationLoaderFactory),
                deps: [HttpClient]
            },
            //defaultLanguage: 'en',
            fallbackLang: 'en',
            missingTranslationHandler: { provide: MissingTranslationHandler, useClass: SpotterMissingTranslationHandler },
        }).providers!,
    ];
}




/** Returns all the shared routes for pages such as authentication */
function addSpotterRoutes(site: SiteShorthand): Route[] {
    return [
        {
            path: 'login', canActivate: [LoggedOutGuard],
            loadComponent: () => import('@spottercomponents/pages/auth/login/login.page').then(m => m.LoginPage),
        },
        {
            path: 'register', canActivate: [LoggedOutGuard],
            loadComponent: () => import('@spottercomponents/pages/auth/register-page/register.page').then(m => m.RegisterPage),
        },
        {
            path: 'forgot', canActivate: [LoggedOutGuard],
            loadComponent: () => import('@spottercomponents/pages/auth/forgot-password/forgot-password.page').then(m => m.ForgotPasswordPage),
            data: { site: site },
        },
        {
            path: 'resetpassword/:resettoken/:email', canActivate: [LoggedOutGuard],
            loadComponent: () => import('@spottercomponents/pages/auth/reset-password/reset-password.page').then(m => m.ResetPasswordPage),
        },


        {
            path: 'user/:id/edit', canActivate: [IsAdminOrOwnerGuard],
            loadComponent: () => import('@spottercomponents/pages/auth/edit-profile/edit-profile.page').then(m => m.EditProfilePage),
        },


        {
            path: 'attachment/:id',
            loadComponent: () => import('@spottercomponents/pages/files/view-attachment/view-attachment.page').then(m => m.ViewAttachmentPage),
        },


        { path: 'terms', loadComponent: () => import('@spottercomponents/pages/general/terms-page/terms-page.component').then(m => m.TermsPageComponent) },
        { path: '**', loadComponent: () => import('@spottercomponents/pages/general/error-not-found-page/error-not-found-page.component').then(m => m.ErrorNotFoundPageComponent) },
    ];
}

/** Sets up the app routes, wrapping the given routes and adding the shared spotter routes.
 *  If you want a custom layout, such as adding to the sidebar, you can provide your own layout component loader (e.g., to load a custom AppLayoutComponent that extends the default one and adds extra functionality). Otherwise, it will use the default AppLayoutComponent.
*/
function setupAppRoutes(routes: Routes, layoutData: AppLayoutData, layoutComponentLoader?: () => Promise<any>): Route {
    return {
        path: '',
        loadComponent: layoutComponentLoader || (() => import('@spotter/components/layout/app-layout/app-layout.component').then(m => m.AppLayoutComponent)),
        data: {
            ...layoutData
        },
        children: [
            ...routes,
            ...addSpotterRoutes(layoutData.site),
        ]
    }
}

/** Creates a routes file to plug into the site.
 *  Handles setting up the app layout and adding shared routes such as authentication.
 *  Optionally include standalone routes that are not wrapped in the app layout (e.g., for helpers or embedded components)
 */
export function setupRoutes(routes: Routes, layoutData: AppLayoutData, standaloneRoutes?: Routes, layoutComponentLoader?: () => Promise<any>): Routes {
    let appRoutes: Routes = [];

    if (standaloneRoutes)
        appRoutes.push(...standaloneRoutes);

    appRoutes.push(setupAppRoutes(routes, layoutData, layoutComponentLoader));

    return appRoutes;
}